import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Chip from '@mui/material/Chip';
import Autocomplete from '@mui/material/Autocomplete';
import Stack from '@mui/material/Stack';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import { useAuth } from '../contexts/AuthContext';
import { firmService, Firm } from '../services/firmService';
import { api, projectAPI, userAPI } from '../services/api';
import { caseService } from '../services/caseService';

interface Meta {
  statuses: Record<string, string>;
  priorities: Record<string, string>;
  types: Record<string, string>;
}

const CaseForm: React.FC<{ mode?: 'create' | 'edit' }> = ({ mode = 'create' }) => {
  const { firmId, caseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [meta, setMeta] = useState<Meta>({ statuses: {}, priorities: {}, types: {} });
  const [projects, setProjects] = useState<any[]>([]);
  const [assignees, setAssignees] = useState<any[]>([]);
  const [notifyUsers, setNotifyUsers] = useState<any[]>([]);
  const [vendors, setVendors] = useState<any[]>([]);
  const [vendorUsers, setVendorUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [sharePointUrl, setSharePointUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [form, setForm] = useState<any>({
    title: '',
    subtitle: '',
    project_id: '',
    assigned_to: '',
    status: 'open',
    priority: 'normal',
    type: 'new',
    free_case: false,
    file_number: '',
    estimate_hours: '',
    finalize_estimate: false,
    description: '',
    notify_all: false,
    notify_suppress: false,
    notify_user_ids: [] as number[],
    vendor_id: '',
    vendor_user_id: '',
    due_date: '',
  });
  const [firm, setFirm] = useState<Firm | null>(null);
  const [projectSettings, setProjectSettings] = useState<any>(null);
  const [formError, setFormError] = useState('');

  const isEdit = mode === 'edit';
  const dueDateRequired = projectSettings?.notification_settings?.allow_optional_due_date !== true;

  const loadMeta = async () => {
    const res = await api.get('/cases/meta');
    setMeta(res.data.data);
  };

  const loadProjects = async () => {
    const res = await projectAPI.getAll({ firm_id: firmId });
    const data = res.data.data?.data || res.data.data || [];
    setProjects(data);
  };

  const hasClientRole = () => {
    if (!user?.roles) return false;
    const clientRoleSlugs = ['client_admin', 'client_user', 'client_finance_user'];
    return user.roles.some(role => clientRoleSlugs.includes(role.slug));
  };

  const isPmpAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'pmp_admin');
  };

  const isSiteAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'site_admin');
  };

  const isPmpAdminOrSiteAdmin = () => {
    return isPmpAdmin() || isSiteAdmin();
  };

  const loadAssignees = async () => {
    const roles = ['client_admin','client_user','client_manager', 'medical_user', 'internal_staff', 'pmp_admin', 'site_admin'];
    const listRes = await api.get('/users', { params: { firm_id: firmId, roles } });
    setAssignees(listRes.data.data || []);
  };

  const loadFirm = async () => {
    if (!firmId) return;
    try {
      const res = await firmService.getFirmById(Number(firmId));
      setFirm(res.data);
    } catch (_) {}
  };

  const loadVendors = async () => {
    if (!isPmpAdminOrSiteAdmin()) return;
    try {
      const res = await api.get('/firms', { params: { type: 'vendor', status: 'approved' } });
      const data = res.data.data?.data || res.data.data || [];
      setVendors(data);
    } catch (err) {
      console.error('Failed to load vendors:', err);
    }
  };

  const loadVendorUsers = async (vendorFirmId: number) => {
    if (!vendorFirmId) {
      setVendorUsers([]);
      return;
    }
    try {
      const res = await api.get('/users', { params: { firm_id: vendorFirmId, roles: ['vendor_user', 'vendor_staff', 'vendor_admin'] } });
      setVendorUsers(res.data.data || []);
    } catch (err) {
      console.error('Failed to load vendor users:', err);
      setVendorUsers([]);
    }
  };

  const loadProjectSettings = async (projectId: string | number) => {
    if (!projectId) {
      setProjectSettings(null);
      return;
    }
    try {
      const res = await projectAPI.getSettings(Number(projectId));
      setProjectSettings(res.data.data || res.data);
    } catch {
      setProjectSettings(null);
    }
  };

  const loadCase = async () => {
    if (!caseId) return;
    const res = await caseService.getCaseById(Number(caseId));
    const c = res.data as any;
    setForm({
      title: c.title || '',
      subtitle: c.subtitle || '',
      project_id: c.project_id,
      assigned_to: c.assigned_to?.id || '',
      status: c.status || 'open',
      priority: c.priority || 'normal',
      type: c.type || 'new',
      free_case: Boolean(c.free_case),
      file_number: c.file_number || '',
      estimate_hours: c.estimate?.estimate_hours || '',
      finalize_estimate: Boolean(c.estimate?.finalize_estimate),
      description: c.description || '',
      notify_all: false,
      notify_suppress: false,
      notify_user_ids: [],
      due_date: c.due_date ? String(c.due_date).slice(0, 10) : '',
    });
    if (c.project_id) {
      loadProjectSettings(c.project_id);
    }
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      loadMeta(), 
      loadProjects(), 
      loadAssignees(), 
      loadFirm(), 
      loadVendors(),
      isEdit ? loadCase() : Promise.resolve()
    ]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmId, caseId]);

  useEffect(() => {
    if (form.project_id) {
      loadProjectSettings(form.project_id);
    } else {
      setProjectSettings(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.project_id]);

  useEffect(() => {
    if (form.vendor_id) {
      loadVendorUsers(form.vendor_id);
    } else {
      setVendorUsers([]);
      handleChange('vendor_user_id', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.vendor_id]);

  const handleChange = (key: string, value: any) => setForm((p: any) => ({ ...p, [key]: value }));

  const handleSubmit = async () => {
    if (dueDateRequired && !form.due_date) {
      setFormError('Due date is required for cases in this project.');
      return;
    }
    setFormError('');

    setSaving(true);
    setIsUploading(attachments.length > 0);
    
    // Initialize upload progress for all files
    if (attachments.length > 0) {
      const initialProgress: Record<string, number> = {};
      attachments.forEach((file) => {
        initialProgress[file.name] = 0;
      });
      setUploadProgress(initialProgress);
    }
    
    try {
      const trimmedSharePointUrl = sharePointUrl.trim();
      const payload = {
        ...form,
        attachments,
        ...(trimmedSharePointUrl && !isEdit ? { sharepoint_url: trimmedSharePointUrl } : {}),
      };
      if (isEdit) {
        await caseService.updateCase(Number(caseId), payload);
      } else {
        const result = await caseService.createCase(
          payload,
          attachments.length > 0 ? (fileName: string, progress: number) => {
            setUploadProgress((prev) => ({ ...prev, [fileName]: progress }));
          } : undefined
        );
        if (result?.sharepoint_import_queued) {
          // Backend already queued ImportSharePointFilesJob into Initial Records
        }
      }
      navigate(`/firms/${firmId}`);
    } catch (error: any) {
      console.error('Failed to save case:', error);
      // Error handling can be added here (e.g., show toast notification)
    } finally { 
      setSaving(false);
      setIsUploading(false);
      setUploadProgress({});
    }
  };

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          color="inherit"
          href="#"
          onClick={(e) => { e.preventDefault(); navigate('/firms'); }}
          sx={{ textDecoration: 'none' }}
        >
          Firms
        </Link>
        {firm && (
          <Link
            color="inherit"
            href="#"
            onClick={(e) => { e.preventDefault(); navigate(`/firms/${firmId}`); }}
            sx={{ textDecoration: 'none' }}
          >
            {firm.name}
          </Link>
        )}
        <Typography color="text.primary">{isEdit ? 'Edit Case' : 'Create Case'}</Typography>
      </Breadcrumbs>
      <Stack spacing={3}>
        {formError && <Typography color="error">{formError}</Typography>}
        <Card>
          <CardHeader title="Case" />
          <Divider />
          <CardContent>
            <Stack spacing={2}>
              <TextField label="Case Title" value={form.title} onChange={(e) => handleChange('title', e.target.value)} fullWidth required />
              <TextField label="Description" value={form.description} onChange={(e) => handleChange('description', e.target.value)} fullWidth multiline minRows={6} />
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardHeader title="Task Information" />
          <Divider />
          <CardContent>
            <Stack spacing={2}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <TextField label="Project" select required value={form.project_id} onChange={(e) => handleChange('project_id', e.target.value)} fullWidth>
                  <MenuItem value="">- Select a value -</MenuItem>
                  {projects.map((p) => (
                    <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                  ))}
                </TextField>
                {!hasClientRole() && (
                  <TextField label="Assigned to" select value={form.assigned_to} onChange={(e) => handleChange('assigned_to', e.target.value)} fullWidth>
                    <MenuItem value="">- None -</MenuItem>
                    {assignees.map((u) => (
                      <MenuItem key={u.id} value={u.id}>{u.name} ({u.email})</MenuItem>
                    ))}
                  </TextField>
                )}
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
                {!hasClientRole() && (
                <TextField label="Status" select required value={form.status} onChange={(e) => handleChange('status', e.target.value)} fullWidth>
                  {Object.entries(meta.statuses).map(([k, v]) => (
                    <MenuItem key={k} value={k}>{v}</MenuItem>
                  ))}
                </TextField>
                )}
                <TextField label="Priority" select required value={form.priority} onChange={(e) => handleChange('priority', e.target.value)} fullWidth>
                  {Object.entries(meta.priorities).map(([k, v]) => (
                    <MenuItem key={k} value={k}>{v}</MenuItem>
                  ))}
                </TextField>
                <TextField label="Type" select required value={form.type} onChange={(e) => handleChange('type', e.target.value)} fullWidth>
                  {Object.entries(meta.types).map(([k, v]) => (
                    <MenuItem key={k} value={k}>{v}</MenuItem>
                  ))}
                </TextField>
              </Box>
              <TextField
                label="Due Date"
                type="date"
                value={form.due_date}
                onChange={(e) => handleChange('due_date', e.target.value)}
                fullWidth
                required={dueDateRequired}
                InputLabelProps={{ shrink: true }}
                helperText={dueDateRequired ? 'Required for this project' : 'Optional for this project'}
              />
            </Stack>
          </CardContent>
        </Card>

        {!hasClientRole() && (
        <Card>
          <CardHeader title="Case Settings" />
          <Divider />
          <CardContent>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <FormControlLabel control={<Checkbox checked={form.free_case} onChange={(e) => handleChange('free_case', e.target.checked)} />} label="Free Case?" />
              <TextField label="Sub Title" value={form.subtitle} onChange={(e) => handleChange('subtitle', e.target.value)} fullWidth />
              <TextField label="File Number" value={form.file_number} onChange={(e) => handleChange('file_number', e.target.value)} fullWidth />
            </Box>
          </CardContent>
        </Card>
        )}

        {isPmpAdminOrSiteAdmin() && !isEdit && (
          <Card>
            <CardHeader 
              title="Vendor Assignment" 
              subheader="Assign this case to a vendor during creation (Admin only)" 
            />
            <Divider />
            <CardContent>
              <Stack spacing={2}>
                <TextField 
                  label="Assign to Vendor" 
                  select 
                  value={form.vendor_id} 
                  onChange={(e) => handleChange('vendor_id', e.target.value)} 
                  fullWidth
                  helperText="Optional: Select a vendor firm to assign this case to"
                >
                  <MenuItem value="">- None -</MenuItem>
                  {vendors.map((v) => (
                    <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>
                  ))}
                </TextField>
                {form.vendor_id && (
                  <>
                    <TextField 
                      label="Vendor User" 
                      select 
                      value={form.vendor_user_id} 
                      onChange={(e) => handleChange('vendor_user_id', e.target.value)} 
                      fullWidth
                      helperText="Optional: Select a specific user at the vendor firm"
                    >
                      <MenuItem value="">- None -</MenuItem>
                      {vendorUsers.map((u) => (
                        <MenuItem key={u.id} value={u.id}>{u.name} ({u.email})</MenuItem>
                      ))}
                    </TextField>
                  </>
                )}
              </Stack>
            </CardContent>
          </Card>
        )}

        

        <Card>
          <CardHeader title="Attachments" />
          <Divider />
          <CardContent>
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Upload local files into Initial Records
                </Typography>
                <input
                  type="file"
                  multiple
                  onChange={(e) => {
                    const files = e.target.files ? Array.from(e.target.files) : [];
                    setAttachments(files);
                  }}
                  accept=".jpg,.jpeg,.gif,.png,.txt,.doc,.docx,.xls,.xlsx,.pdf,.ppt,.pptx,.pps,.ppsx,.odt,.ods,.odp,.mp3,.mov,.mp4,.m4a,.m4v,.mpeg,.avi,.ogg,.oga,.ogv,.weba,.webp,.webm,.zip,.tgz,.gz,.key,.dotx"
                />
                {attachments.length > 0 && (
                  <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                    {attachments.map((f, idx) => (
                      <Chip 
                        key={idx} 
                        label={f.name} 
                        onDelete={() => {
                          const newAttachments = attachments.filter((_, i) => i !== idx);
                          setAttachments(newAttachments);
                        }}
                      />
                    ))}
                  </Stack>
                )}
              </Box>

              {!isEdit && (
                <TextField
                  label="SharePoint / OneDrive folder link"
                  placeholder="https://....sharepoint.com/:f:/..."
                  value={sharePointUrl}
                  onChange={(e) => setSharePointUrl(e.target.value)}
                  fullWidth
                  helperText="Optional. Paste a doctor folder sharing link to import into Initial Records after the case is created. You will be emailed when the import finishes."
                />
              )}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardHeader title="Notification" />
          <Divider />
          <CardContent>
            <Stack spacing={1}>
              <FormControlLabel control={<Checkbox checked={form.notify_all} onChange={(e) => handleChange('notify_all', e.target.checked)} />} label="Send notification to all users" />
              <FormControlLabel control={<Checkbox checked={form.notify_suppress} onChange={(e) => handleChange('notify_suppress', e.target.checked)} />} label="Do not send notification for this update" />
              <Autocomplete
                multiple
                options={assignees}
                value={assignees.filter((u) => form.notify_user_ids.includes(u.id))}
                onChange={(_e, val) => handleChange('notify_user_ids', val.map((v) => v.id))}
                getOptionLabel={(opt) => `${opt.name} (${opt.email}${opt.username ? ` | ${opt.username}` : ''})`}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option.name} {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => <TextField {...params} label="Select users to notify" placeholder="Search users" />}
              />
            </Stack>
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" onClick={() => navigate(-1)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>
            {saving ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <span>{isUploading ? 'Uploading...' : 'Saving...'}</span>
              </Box>
            ) : (
              'Submit'
            )}
          </Button>
        </Box>
      </Stack>

      {/* Upload Progress Dialog */}
      <Dialog 
        open={isUploading && Object.keys(uploadProgress).length > 0} 
        maxWidth="sm" 
        fullWidth
        disableEscapeKeyDown
        onClose={() => {}} // Prevent closing during upload
      >
        <DialogTitle>Uploading Files</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {Object.entries(uploadProgress).map(([fileName, progress]) => (
              <Box key={fileName} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mr: 1 }}>
                    {fileName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {progress}%
                  </Typography>
                </Box>
                <LinearProgress variant="determinate" value={progress} />
              </Box>
            ))}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CaseForm;
